
import { Node, Edge } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  parseKPIContent, 
  getNodePositionsForKPIType,
  KpiData
} from "./utils";
import React from 'react';

interface Account {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
}

// Helper function to create node label
const createNodeLabel = (title: string, description: string) => {
  return {
    type: 'node-label',
    title,
    description
  };
};

// Helper function to extract meaningful title and description from KPI content WITHOUT any truncation
const extractTitleAndDescription = (kpiContent: string, fallbackTitle: string) => {
  if (!kpiContent || kpiContent.trim().length === 0) {
    return {
      title: fallbackTitle,
      description: 'Data not available from current analysis'
    };
  }

  // Split content into sentences for better processing
  const sentences = kpiContent.split(/[.!?]+/).filter(s => s.trim().length > 0);
  
  if (sentences.length === 0) {
    return {
      title: fallbackTitle,
      description: kpiContent // Keep full content, no truncation
    };
  }

  // Extract first meaningful phrase as title but don't truncate
  let title = sentences[0].trim();
  const titleBreakPoints = [',', ':', '-', '('];
  for (const breakPoint of titleBreakPoints) {
    if (title.includes(breakPoint)) {
      title = title.split(breakPoint)[0].trim();
      break;
    }
  }
  
  // NO TRUNCATION - keep full content
  let description = kpiContent;

  return {
    title: title || fallbackTitle,
    description: description || 'Additional details not available'
  };
};

// Process raw KPI data into exactly 5 nodes with full content preserved (NO TRUNCATION)
const processKpiArray = (kpiArray: any[], kpiType: string): any[] => {
  const processed = [];
  
  if (Array.isArray(kpiArray) && kpiArray.length > 0) {
    // Process each item in the array
    for (let i = 0; i < Math.min(kpiArray.length, 5); i++) {
      const item = kpiArray[i];
      const content = typeof item === 'string' ? item : String(item || '');
      const { title, description } = extractTitleAndDescription(content, `${kpiType} ${i + 1}`);
      
      processed.push({
        title, // NO truncation
        description, // NO truncation
        fullDescription: content // Keep full original content
      });
    }
  }
  
  // Fill remaining slots up to 5 with meaningful placeholders
  while (processed.length < 5) {
    const index = processed.length + 1;
    processed.push({
      title: `${kpiType} ${index}`,
      description: 'Data not available from current analysis',
      fullDescription: 'Data not available from current analysis'
    });
  }
  
  return processed;
};

// Process KPI data and create nodes
export const processKpiData = (
  data: KpiData, 
  baseNodeId: number = 1000,
  handleNodeDoubleClick: (event: React.MouseEvent, nodeId: string) => void
): Node[] => {
  if (!data) return [];
  
  let newNodes: Node[] = [];
  let nextNodeId = baseNodeId;
  
  // Process Executive KPIs
  if (data.exec_kpis) {
    const execKpisArray = Array.isArray(data.exec_kpis) 
      ? data.exec_kpis 
      : (typeof data.exec_kpis === 'string' ? [data.exec_kpis] : []);
    
    const processedExecKpis = processKpiArray(execKpisArray, 'Executive KPI');
    
    processedExecKpis.forEach((kpi, index) => {
      const position = getNodePositionsForKPIType('executive', newNodes, 5, index);
      
      newNodes.push({
        id: `kpi-exec-${nextNodeId++}`,
        type: 'default',
        data: {
          label: createNodeLabel(kpi.title, kpi.description),
          kpiType: 'Executive KPIs',
          title: kpi.title,
          description: kpi.description,
          fullDescription: kpi.fullDescription,
          onNodeDoubleClick: handleNodeDoubleClick
        },
        position,
        style: {
          width: 200, // Fixed width
          height: 80, // Fixed height
          minWidth: 200,
          maxWidth: 200,
          minHeight: 80,
          maxHeight: 80,
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px'
        }
      });
    });
  }
  
  // Process Business Initiatives
  if (data.business_initiatives) {
    const businessInitiativesArray = Array.isArray(data.business_initiatives) 
      ? data.business_initiatives 
      : (typeof data.business_initiatives === 'string' ? [data.business_initiatives] : []);
    
    const processedInitiatives = processKpiArray(businessInitiativesArray, 'Business Initiative');
    
    processedInitiatives.forEach((initiative, index) => {
      const position = getNodePositionsForKPIType('initiatives', newNodes, 5, index);
      
      newNodes.push({
        id: `kpi-initiative-${nextNodeId++}`,
        type: 'default',
        data: {
          label: createNodeLabel(initiative.title, initiative.description),
          kpiType: 'Business Initiatives',
          title: initiative.title,
          description: initiative.description,
          fullDescription: initiative.fullDescription,
          onNodeDoubleClick: handleNodeDoubleClick
        },
        position,
        style: {
          width: 200, // Fixed width
          height: 80, // Fixed height
          minWidth: 200,
          maxWidth: 200,
          minHeight: 80,
          maxHeight: 80,
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px'
        }
      });
    });
  }
  
  // Process Core Financial KPIs
  if (data.core_financial_kpis) {
    const financialKpisArray = Array.isArray(data.core_financial_kpis) 
      ? data.core_financial_kpis 
      : (typeof data.core_financial_kpis === 'string' ? [data.core_financial_kpis] : []);
    
    const processedFinancials = processKpiArray(financialKpisArray, 'Financial KPI');
    
    processedFinancials.forEach((financial, index) => {
      const position = getNodePositionsForKPIType('financial', newNodes, 5, index);
      
      newNodes.push({
        id: `kpi-financial-${nextNodeId++}`,
        type: 'default',
        data: {
          label: createNodeLabel(financial.title, financial.description),
          kpiType: 'Core Financial KPIs',
          title: financial.title,
          description: financial.description,
          fullDescription: financial.fullDescription,
          onNodeDoubleClick: handleNodeDoubleClick
        },
        position,
        style: {
          width: 200, // Fixed width
          height: 80, // Fixed height
          minWidth: 200,
          maxWidth: 200,
          minHeight: 80,
          maxHeight: 80,
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px'
        }
      });
    });
  }
  
  // Process Operating KPIs
  if (data.operating_kpis) {
    const operatingKpisArray = Array.isArray(data.operating_kpis) 
      ? data.operating_kpis 
      : (typeof data.operating_kpis === 'string' ? [data.operating_kpis] : []);
    
    const processedOperating = processKpiArray(operatingKpisArray, 'Operating KPI');
    
    processedOperating.forEach((operating, index) => {
      const position = getNodePositionsForKPIType('operating', newNodes, 5, index);
      
      newNodes.push({
        id: `kpi-operating-${nextNodeId++}`,
        type: 'default',
        data: {
          label: createNodeLabel(operating.title, operating.description),
          kpiType: 'Operating KPIs',
          title: operating.title,
          description: operating.description,
          fullDescription: operating.fullDescription,
          onNodeDoubleClick: handleNodeDoubleClick
        },
        position,
        style: {
          width: 200, // Fixed width
          height: 80, // Fixed height
          minWidth: 200,
          maxWidth: 200,
          minHeight: 80,
          maxHeight: 80,
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px'
        }
      });
    });
  }
  
  return newNodes;
};

// Helper function to check if a record already exists based on content
const recordExists = (existingRecords: any[], kpiType: string, title: string, description: string): boolean => {
  return existingRecords.some(record => 
    record.kpi_type === kpiType &&
    record.title === title &&
    record.description === description
  );
};

// IMPROVED: Save nodes with content-based duplicate prevention
export const saveKpiNodesToFramework = async (kpiNodes: Node[], companyId: string, accountOwnerUserId?: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No authenticated user for saveKpiNodesToFramework');
      return;
    }
    
    const userIdToUse = accountOwnerUserId || user.id;
    console.log('Starting saveKpiNodesToFramework for user:', userIdToUse, 'company:', companyId);
    
    // Get existing KPI records to check for duplicates based on content
    const { data: existingRecords, error: fetchError } = await supabase
      .from('business_value_framework')
      .select('id, title, description, kpi_type, position_x, position_y')
      .eq('company_id', companyId)
      .eq('user_id', userIdToUse)
      .in('kpi_type', ['Executive KPIs', 'Core Financial KPIs', 'Business Initiatives', 'Operating KPIs']);

    if (fetchError) {
      console.error('Error fetching existing records:', fetchError);
      throw fetchError;
    }

    console.log('Found existing KPI records:', existingRecords?.length || 0);

    // Process each node with content-based duplicate prevention
    for (let index = 0; index < kpiNodes.length; index++) {
      const node = kpiNodes[index];
      
      let kpiType = '';
      const idParts = node.id.match(/kpi-(\w+)-(\d+)/);
      
      if (idParts && idParts[1]) {
        switch(idParts[1]) {
          case 'exec': kpiType = 'Executive KPIs'; break;
          case 'financial': kpiType = 'Core Financial KPIs'; break;
          case 'initiative': kpiType = 'Business Initiatives'; break;
          case 'operating': kpiType = 'Operating KPIs'; break;
          default: kpiType = 'Potential Solutions';
        }
      }
      
      const nodeData = node.data;
      const title = String(nodeData.title || '');
      const description = String(nodeData.description || '');
      
      // Check if a record with the same content already exists
      if (recordExists(existingRecords || [], kpiType, title, description)) {
        console.log(`Record already exists: ${kpiType} - ${title}`);
        continue; // Skip this record as it already exists
      }

      // Find existing record with same kpi_type but different content for update
      const existingRecord = existingRecords?.find(record => 
        record.kpi_type === kpiType &&
        Math.abs((record.position_x || 0) - node.position.x) < 50 &&
        Math.abs((record.position_y || 0) - node.position.y) < 50 &&
        (record.title !== title || record.description !== description)
      );

      if (existingRecord) {
        console.log(`Updating existing record ${existingRecord.id} for ${kpiType}`);
        
        // Update existing record with new content
        const { error: updateError } = await supabase
          .from('business_value_framework')
          .update({
            title: title,
            description: description,
            position_x: node.position.x,
            position_y: node.position.y,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRecord.id);
          
        if (updateError) {
          console.error('Error updating existing record:', updateError);
        }
      } else {
        console.log(`Creating new record for ${kpiType} - ${title}`);
        
        // Insert new record
        const { error: insertError } = await supabase
          .from('business_value_framework')
          .insert({
            company_id: companyId,
            title: title,
            description: description,
            position_x: node.position.x,
            position_y: node.position.y,
            kpi_type: kpiType,
            user_id: userIdToUse
          });
          
        if (insertError) {
          console.error('Error inserting new record:', insertError);
        }
      }
    }
    
    console.log("Successfully completed saveKpiNodesToFramework with content-based duplicate prevention");
  } catch (error) {
    console.error('Error in saveKpiNodesToFramework:', error);
    toast.error("Failed to save KPI data to business value framework");
  }
};

// Fetch KPI data from customer_kpis table
export const fetchKpiData = async (companyId: string, accountOwnerUserId?: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    // Use provided accountOwnerUserId or fallback to current user
    const userIdToQuery = accountOwnerUserId || user.id;
    
    const { data, error } = await supabase
      .from('customer_kpis')
      .select('*')
      .eq('company_id', companyId)
      .eq('user_id', userIdToQuery) // Query with the correct user ID
      .order('updated_at', { ascending: false })
      .limit(1);
      
    if (error) throw error;
    
    // If data exists, return it
    if (data && data.length > 0) {
      return data[0];
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching KPI data:', error);
    return null;
  }
};
